import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {CountryService} from '../../../../../data-repositories/country-service';
import {Country, StateInfo} from '../../../../../models/country';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {FocusInvalidService} from '../../../../../services/focus-invalid.service';
import {validatePOBox} from '../../../../../services/validators';
import {IAddressInfo} from '../../../../../models/address-info';
import {MonitoringType} from '../../../../../models/monitoring-type';
import {isEUCountry} from '../../../../../data-repositories/eu-countries';
import {formLog} from '../../../../../services/debug-logger';

@Component({
  standalone: false,
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss']
})
export class AddressComponent implements OnInit, OnDestroy {
  public countryList: Array<Country>;
  public addressForm: FormGroup;
  public submitted: boolean;
  @ViewChild('contactFormElement') contactFormElement: ElementRef;
  @Output() addressModelChange = new EventEmitter<IAddressInfo>();
  @Input() monitoring: MonitoringType;
  private nonWhitespaceRegExp: RegExp = new RegExp('\\S');
  private subscription: Subscription;
  private formSub = new Subscription();

  constructor(
    countryService: CountryService,
    private formBuilder: FormBuilder,
    private validationService: ValidationInfoService,
    private focusInvalidService: FocusInvalidService,
    private route: ActivatedRoute) {

    this.submitted = false;
    this.buildForm();

    this.formSub.add(countryService.getCountryList().subscribe(cl => {
      this.countryList = cl;

      this.onAddressCountryChanged(false);
    }));
  }

  private _addressModel: IAddressInfo;

  get addressModel(): IAddressInfo {
    return this._addressModel;
  }

  @Input()
  set addressModel(value: IAddressInfo) {
    this._addressModel = value;
    this.patchFormFromModel();
    this.onAddressCountryChanged(false);
  }

  ngOnInit() {
    this.formSub.add(this.route.queryParams.subscribe(params => {
      if (params.validate) {
        this.markAllFieldsAsTouched();
        this.focusInvalidService.selectInvalidInput(this.contactFormElement);
      }
    }));

    this.subscription = this.validationService.onUserUpdate.subscribe(() => {
      this.submitted = true;
      this.markAllFieldsAsTouched();

      this.focusInvalidService.selectInvalidInput(this.contactFormElement);
      formLog.invalid('Address', this.addressForm);
      this.validationService.validate(this.addressForm.valid, 'AddressComponent');
    });
    this.formSub.add(this.addressForm.valueChanges.subscribe(() => this.syncModelFromForm()));
    this.subscribeAllInputsOnUpdate();
  }

  private patchFormFromModel(): void {
    if (!this._addressModel || !this.addressForm) {
      return;
    }
    this.addressForm.patchValue({
      addressCountry: this._addressModel.countryName ?? '',
      addressCity: this._addressModel.city ?? '',
      address1: this._addressModel.address1 ?? '',
      address2: this._addressModel.address2 ?? '',
      addressPostalCode: this._addressModel.postalCode ?? '',
      addressState: this._addressModel.state ?? '',
    }, {emitEvent: false});
    formLog.state('Address', this._addressModel, this.addressForm);
  }

  private syncModelFromForm(): void {
    if (!this._addressModel) {
      return;
    }
    const value = this.addressForm.getRawValue();
    this._addressModel.countryName = value.addressCountry;
    this._addressModel.city = value.addressCity;
    this._addressModel.address1 = value.address1;
    this._addressModel.address2 = value.address2;
    this._addressModel.postalCode = value.addressPostalCode;
    this._addressModel.state = value.addressState;
    formLog.state('Address', this._addressModel, this.addressForm);
  }

  buildForm() {
    this.addressForm = this.formBuilder.group({
      addressCountry: new FormControl('', Validators.required),
      address1: new FormControl('', [Validators.required, Validators.pattern(this.nonWhitespaceRegExp), validatePOBox()]),
      addressState: new FormControl('', Validators.required),
      address2: new FormControl('', [Validators.required, Validators.pattern(this.nonWhitespaceRegExp)]),
      addressCity: new FormControl('', [Validators.required, Validators.pattern(this.nonWhitespaceRegExp)]),
      addressPostalCode: new FormControl('', [Validators.required, Validators.pattern(this.nonWhitespaceRegExp)]),
    });
  }

  public isRequiredField(fieldName: string): boolean {
    return this.addressForm.controls[fieldName].invalid
      && (this.addressForm.controls[fieldName].dirty || this.addressForm.controls[fieldName].touched)
      && (this.addressForm.controls[fieldName].errors.required || this.addressForm.controls[fieldName].errors.pattern);
  }

  public isInvalidPOBoxField(fieldName: string): boolean {
    return this.addressForm.controls[fieldName].invalid
      && ((this.addressForm.controls[fieldName].dirty || this.addressForm.controls[fieldName].touched) || this.submitted)
      && this.addressForm.controls[fieldName].hasError('poBox');
  }

  public hasErrorField(fieldName: string, error: string): boolean {
    const control = this.addressForm.controls[fieldName];
    return control.invalid &&
      ((control.dirty || control.touched) || this.submitted) &&
      control.hasError(error);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.formSub.unsubscribe();
  }

  subscribeAllInputsOnUpdate() {
    const addressStage = document.getElementById('addressStage');

    const inputs = addressStage.getElementsByTagName('input');

    for (let inputIndex = 0; inputIndex < inputs.length; inputIndex++) {
      inputs.item(inputIndex).addEventListener('change', () => {
        this.updateUser();
      });
    }
  }

  public updateUser(): void {
    this.addressModelChange.emit(this.addressModel);
  }

  filterCountryList(countryList: Array<Country>, filter: string, includeUSA: boolean = true) {
    if (!countryList) {
      return new Array<Country>();
    }

    if (filter && filter.trim().length > 0) {
      return countryList.filter(x => x.countryName !== 'United States' || includeUSA)
        .filter(x => x.countryTranslation.toLowerCase().startsWith(filter.toLowerCase()));
    }

    return countryList.filter(x => x.countryName !== 'United States' || includeUSA);
  }

  getCountryCode(citizenship: string): string {
    if (citizenship && this.countryList && this.countryList.some(x => x.countryName === citizenship)) {
      return this.countryList.find(x => x.countryName === citizenship).countryCode;
    }

    return '';
  }

  getTranslate(country: string) {
    if (!country || !this.countryList || !this.countryList.some(x => x.countryName === country)) {
      return '';
    }

    return this.countryList.find(x => x.countryName === country).countryTranslation;
  }

  getStatesByCountry(countryName: string): Array<StateInfo> {
    if (countryName && this.countryList) {
      const country = this.countryList.find(x => x.countryName === countryName);
      return country.states;
    }

    return new Array<StateInfo>();
  }

  getStateName(countryName: string, stateCode: string) {
    if (countryName && stateCode) {
      const countryStates = this.getStatesByCountry(countryName);
      if (countryStates.some(x => x.code === stateCode)) {
        return countryStates.find(x => x.code === stateCode).name;
      }
    }

    return '';
  }

  onAddressCountryChanged(reset: boolean = true) {
    if (reset) {
      this.addressModel.state = null;
      this.updateUser();
    }

    const hasStates = !!this.addressModel && this.getStatesByCountry(this.addressModel.countryName).length > 0;
    const state = this.addressForm.controls.addressState;

    if (hasStates) {
      state.enable();
    } else {
      state.disable();
    }
    state.setValidators(hasStates ? Validators.required : null);
    if (reset) {
      state.setValue(null);
    }

    if (hasStates && this.addressModel.countryName === 'Israel') {
      const israelStateCode = this.getStatesByCountry(this.addressModel.countryName)[0].code;
      this.addressModel.state = israelStateCode;
      state.setValue(israelStateCode);
      this.updateUser();
    }
    state.updateValueAndValidity();
  }

  isEUCountryWithUSMonitoring() {
    return this.monitoring === MonitoringType.USA &&
      isEUCountry(this.addressModel.countryName);
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.addressForm.controls).forEach(key => {
      this.addressForm.get(key).setErrors(null);
      this.addressForm.get(key).markAsTouched();
      this.addressForm.get(key).updateValueAndValidity();
    });
    formLog.invalid('Address', this.addressForm);
  }
}
